import Box from 'components/Box';
import React, { useMemo } from 'react';
import styled from 'styled-components';
import { formatCurrency } from 'utils/formatCurrency';
import { ICashDepositPrintByDate, ICashDepositPrintItem } from 'features/report/services/types/cash-deposit';
import moment from 'moment';
import { DATE_FORMAT_VIEW } from 'features/appointment/utils/format';

type ICashDepositReportPrintPreviewProps = {
  data: ICashDepositPrintByDate | null;
  startDate: string;
  endDate: string;
  shopInfo: {
    name: string;
    streetAddress: string;
    cityStateZip: string;
    phone: string;
  };
};

const CashDepositReportPrintPreview: React.FC<ICashDepositReportPrintPreviewProps> = ({
  data,
  startDate,
  endDate,
  shopInfo,
}) => {
  const groupedByDate = useMemo(() => {
    return data || {};
  }, [data]);

  // Format date range or single date
  const formattedDateRange = useMemo(() => {
    if (!startDate || !endDate) return '';
    const start = moment(startDate);
    const end = moment(endDate);
    if (start.isSame(end, 'day')) {
      return start.format('MM-DD-YYYY');
    }
    return `${start.format('MM-DD-YYYY')} - ${end.format('MM-DD-YYYY')}`;
  }, [startDate, endDate]);

  // Check if single date
  const isSingleDate = useMemo(() => {
    if (!startDate || !endDate) return false;
    const start = moment(startDate);
    const end = moment(endDate);
    return start.isSame(end, 'day');
  }, [startDate, endDate]);

  // Calculate total amount
  const totalAmount = useMemo(() => {
    if (!data) return 0;
    return Object.values(data).reduce((dateSum, items) => {
      return dateSum + (items as ICashDepositPrintItem[]).reduce((itemSum, item) => {
        const depositTotal = (item.cashDeposits || []).reduce((cashSum, cash) => cashSum + (cash.total || 0), 0);
        return itemSum + depositTotal;
      }, 0);
    }, 0);
  }, [data]);

  // Sort dates
  const sortedDates = useMemo(
    () =>
      Object.keys(groupedByDate).sort((a, b) => {
        const dateA = moment(a, DATE_FORMAT_VIEW);
        const dateB = moment(b, DATE_FORMAT_VIEW);
        if (dateA.isBefore(dateB)) return -1;
        if (dateA.isAfter(dateB)) return 1;
        return 0;
      }),
    [groupedByDate]
  );

  return (
    <PrintContainer>
      <Box className="center" overflow="auto">
        <TicketBox>
          <HeaderSection>
            <ShopName>{shopInfo.name}</ShopName>
            <ShopAddress>{shopInfo.streetAddress}</ShopAddress>
            <ShopAddress>{shopInfo.cityStateZip}</ShopAddress>
            <ShopPhone>{shopInfo.phone}</ShopPhone>
            <DateText>{formattedDateRange}</DateText>
          </HeaderSection>

          {totalAmount > 0 && (
            <TotalSection>
              <TotalHeader>
                <TotalLabel>AMOUNT</TotalLabel>
                <TotalAmount>{formatCurrency(totalAmount)}</TotalAmount>
              </TotalHeader>
            </TotalSection>
          )}

          <StaffSection>
            {sortedDates.map((date) => {
              const items = groupedByDate[date];
              if (!items || items.length === 0) return null;

              return (
                <DateGroup key={date}>
                  {!isSingleDate && (
                    <DateHeader>
                      <DateHeaderText>{date}</DateHeaderText>
                    </DateHeader>
                  )}
                  <TableSection>
                    <TableHeader>
                      <HeaderCell>STAFF</HeaderCell>
                      <HeaderCell>TIME</HeaderCell>
                      <HeaderCell>AMOUNT</HeaderCell>
                    </TableHeader>
                    {(items as ICashDepositPrintItem[]).map((item, index) => {
                      const time = moment(item.createdDate, 'MM/DD/YYYY hh:mm A').format('hh:mm A');
                      const amount = (item.cashDeposits || []).reduce(
                        (sum, cash) => sum + (cash.total || 0),
                        0
                      );
                      return (
                        <TableRow key={`${item.staffName}-${item.createdDate}-${index}`}>
                          <TableCell>{item.staffName}</TableCell>
                          <TableCell>{time}</TableCell>
                          <TableCell>{formatCurrency(amount)}</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableSection>
                </DateGroup>
              );
            })}
          </StaffSection>
        </TicketBox>
      </Box>
    </PrintContainer>
  );
};

export default CashDepositReportPrintPreview;

const PrintContainer = styled.div`
  font-family: 'Open Sans', Arial, sans-serif;
  width: 100%;
  min-height: 100vh;
  padding: 24px 0;
`;

const TicketBox = styled.div`
  border-radius: 20px;
  background: #fff;
  width: 72mm;
  margin: 0 auto;
  padding: 24px 8px 32px;
`;

const HeaderSection = styled.div`
  text-align: center;
  margin-bottom: 16px;
  padding: 0 8px;
`;

const ShopName = styled.div`
  font-size: 16px;
  font-weight: 700;
  text-transform: uppercase;
  margin-bottom: 2px;
  color: black;
  font-family: 'Open Sans', Arial, sans-serif;
`;

const ShopAddress = styled.div`
  font-size: 12px;
  margin-bottom: 2px;
  color: black;
  font-family: 'Open Sans', Arial, sans-serif;
`;

const ShopPhone = styled.div`
  font-size: 12px;
  margin-bottom: 4px;
  color: black;
  font-family: 'Open Sans', Arial, sans-serif;
`;

const DateText = styled.div`
  font-size: 14px;
  font-weight: bold;
  margin-top: 4px;
  color: black;
  font-family: 'Open Sans', Arial, sans-serif;
`;

const TotalSection = styled.div`
  margin-bottom: 16px;
  padding: 0 8px;
`;

const TotalHeader = styled.div`
  background-color: black;
  color: white;
  padding: 8px 0px;
  font-weight: bold;
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 14px;
  font-family: 'Open Sans', Arial, sans-serif;
  text-transform: uppercase;
`;

const TotalLabel = styled.div`
  padding: 0 8px;
  font-size: 14px;
`;

const TotalAmount = styled.div`
  padding: 0 8px;
  font-size: 14px;
  text-align: right;
`;

const StaffSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 0 8px;
`;

const DateGroup = styled.div`
  border-bottom: 1px dashed black;
  padding-bottom: 8px;
  margin-bottom: 8px;
  
  &:last-child {
    border-bottom: none;
    margin-bottom: 0;
  }
`;

const DateHeader = styled.div`
  background-color: black;
  color: white;
  padding: 4px 8px;
  font-weight: bold;
  font-size: 16px;
  font-family: 'Open Sans', Arial, sans-serif;
  margin-bottom: 4px;
  height: 24px;
  display: flex;
  align-items: center;
`;

const DateHeaderText = styled.div`
  line-height: 24px;
  font-size: 16px;
`;

const TableSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const TableHeader = styled.div`
  display: flex;
  padding: 6px 8px;
  border-bottom: 1px dashed black;
  font-weight: 600;
  font-size: 12px;
  font-family: 'Open Sans', Arial, sans-serif;
`;

const HeaderCell = styled.div`
  flex: 1;
  font-size: 12px;
  color: black;
  text-align: left;
  padding: 0;
  line-height: 19.5px;
  font-family: 'Open Sans', Arial, sans-serif;
  
  &:nth-child(2) {
    text-align: right;
  }
  
  &:last-child {
    text-align: right;
  }
`;

const TableRow = styled.div`
  display: flex;
  padding: 4px 8px;
  font-size: 12px;
`;

const TableCell = styled.div`
  flex: 1;
  font-family: 'Open Sans', Arial, sans-serif;
  font-size: 12px;
  color: black;
  text-align: left;
  line-height: 19.5px;
  
  &:nth-child(2) {
    text-align: right;
  }
  
  &:last-child {
    text-align: right;
  }
`;
